import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { 
  Save, Loader2, Layout, Eye, EyeOff, GripVertical, 
  Layers, Palette, Grid3X3, Check, RotateCcw
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const LayoutEditor = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [predefinedLayouts, setPredefinedLayouts] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [currentLayout, setCurrentLayout] = useState(null);
  const [sections, setSections] = useState([]);
  const [style, setStyle] = useState({
    cardStyle: 'rounded',
    spacing: 'comfortable',
    sectionBackground: 'solid',
    animationStyle: 'subtle'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLayoutId, setSelectedLayoutId] = useState('classic');

  useEffect(() => {
    fetchLayouts();
  }, []);

  const fetchLayouts = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getPredefinedLayouts(token);
      if (response.success) {
        setPredefinedLayouts(response.data.layouts);
        setAllSections(response.data.allSections);
        
        // Fetch current layout settings
        const layoutRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/layout`);
        const layoutData = await layoutRes.json();
        
        if (layoutData.success && layoutData.data) {
          setCurrentLayout(layoutData.data);
          setSections(layoutData.data.sections || response.data.allSections);
          setStyle(layoutData.data.style || {
            cardStyle: 'rounded',
            spacing: 'comfortable',
            sectionBackground: 'solid',
            animationStyle: 'subtle'
          });
          setSelectedLayoutId(layoutData.data.layoutName || 'classic');
        } else {
          setSections(response.data.allSections);
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load layouts',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update order values
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));
    
    // Ensure hero stays at top and visible
    const heroIndex = updatedItems.findIndex(s => s.id === 'hero');
    if (heroIndex > 0) {
      const [hero] = updatedItems.splice(heroIndex, 1);
      hero.order = 0;
      hero.visible = true;
      updatedItems.unshift(hero);
      updatedItems.forEach((item, index) => item.order = index);
    }
    
    setSections(updatedItems);
    setSelectedLayoutId('custom');
  };

  const toggleSectionVisibility = (sectionId) => {
    if (sectionId === 'hero') return; // Hero always visible
    
    setSections(prev => prev.map(s => 
      s.id === sectionId ? { ...s, visible: !s.visible } : s
    ));
    setSelectedLayoutId('custom');
  };

  const handleSelectPredefined = (layoutId) => {
    const layout = predefinedLayouts.find(l => l.id === layoutId);
    if (layout) {
      // Merge predefined sections with all available sections
      const layoutSectionIds = layout.sections.map(s => s.id);
      const mergedSections = [
        ...layout.sections,
        ...allSections
          .filter(s => !layoutSectionIds.includes(s.id))
          .map(s => ({ ...s, visible: false, order: layout.sections.length }))
      ];
      
      setSections(mergedSections.map((s, i) => ({ ...s, order: i })));
      setStyle(layout.style);
      setSelectedLayoutId(layoutId);
    }
  };

  const handleStyleChange = (key, value) => {
    setStyle(prev => ({ ...prev, [key]: value }));
    setSelectedLayoutId('custom');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await adminAPI.updateLayout({
        layoutName: selectedLayoutId,
        sections: sections,
        style: style
      }, token);
      
      if (response.success) {
        toast({
          title: 'Layout Saved!',
          description: 'Your layout changes have been applied',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save layout',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    handleSelectPredefined('classic');
    toast({
      title: 'Layout Reset',
      description: 'Reset to classic layout',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-orange-500" size={40} />
      </div>
    );
  }

  const visibleSections = sections.filter(s => s.visible);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Layout Customization</h2>
          <p className="text-gray-400">Choose a layout and drag sections to reorder</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleReset}
            variant="outline"
            className="border-gray-600 text-gray-400 hover:bg-gray-800"
          >
            <RotateCcw className="mr-2" size={18} />
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={18} />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2" size={18} />
                Save Layout
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left Column: Layout Selection & Style */}
        <div className="space-y-6">
          <Accordion type="multiple" defaultValue={["predefined", "sections"]} className="space-y-4">
            {/* Predefined Layouts */}
            <AccordionItem value="predefined" className="glass border border-orange-500/20 rounded-2xl px-6">
              <AccordionTrigger className="text-lg font-semibold text-white hover:text-orange-500">
                <div className="flex items-center gap-2">
                  <Layout size={20} className="text-orange-500" />
                  Predefined Layouts
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {predefinedLayouts.map((layout) => (
                    <button
                      key={layout.id}
                      onClick={() => handleSelectPredefined(layout.id)}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        selectedLayoutId === layout.id
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-gray-700 bg-zinc-900 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">{layout.name}</span>
                        {selectedLayoutId === layout.id && (
                          <Check size={18} className="text-orange-500" />
                        )}
                      </div>
                      <p className="text-gray-400 text-sm">{layout.description}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {layout.sections.slice(0, 4).map(s => (
                          <span key={s.id} className="text-xs bg-zinc-800 text-gray-400 px-2 py-0.5 rounded">
                            {s.name}
                          </span>
                        ))}
                        {layout.sections.length > 4 && (
                          <span className="text-xs bg-zinc-800 text-gray-400 px-2 py-0.5 rounded">
                            +{layout.sections.length - 4}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Section Reorder */}
            <AccordionItem value="sections" className="glass border border-blue-500/20 rounded-2xl px-6">
              <AccordionTrigger className="text-lg font-semibold text-white hover:text-blue-500">
                <div className="flex items-center gap-2">
                  <Layers size={20} className="text-blue-500" />
                  Section Order & Visibility
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <p className="text-gray-400 text-sm mb-4">Drag to reorder. Click the eye icon to show/hide sections.</p>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="sections">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="space-y-2"
                      >
                        {sections.map((section, index) => (
                          <Draggable 
                            key={section.id} 
                            draggableId={section.id} 
                            index={index}
                            isDragDisabled={section.id === 'hero'}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                                  snapshot.isDragging
                                    ? 'border-blue-500 bg-blue-500/20'
                                    : section.visible
                                    ? 'border-gray-700 bg-zinc-900'
                                    : 'border-gray-800 bg-zinc-950 opacity-50'
                                } ${section.id === 'hero' ? 'border-orange-500/50' : ''}`}
                              >
                                <div 
                                  {...provided.dragHandleProps}
                                  className={`cursor-grab ${section.id === 'hero' ? 'opacity-30 cursor-not-allowed' : ''}`}
                                >
                                  <GripVertical size={20} className="text-gray-500" />
                                </div>
                                <span className={`flex-1 font-medium ${section.visible ? 'text-white' : 'text-gray-500'}`}>
                                  {section.name}
                                  {section.id === 'hero' && (
                                    <span className="text-xs text-orange-500 ml-2">(Always visible)</span>
                                  )}
                                </span>
                                <button
                                  onClick={() => toggleSectionVisibility(section.id)}
                                  disabled={section.id === 'hero'}
                                  className={`p-1 rounded hover:bg-gray-800 ${section.id === 'hero' ? 'opacity-30 cursor-not-allowed' : ''}`}
                                >
                                  {section.visible ? (
                                    <Eye size={18} className="text-green-500" />
                                  ) : (
                                    <EyeOff size={18} className="text-gray-500" />
                                  )}
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </AccordionContent>
            </AccordionItem>

            {/* Style Options */}
            <AccordionItem value="style" className="glass border border-purple-500/20 rounded-2xl px-6">
              <AccordionTrigger className="text-lg font-semibold text-white hover:text-purple-500">
                <div className="flex items-center gap-2">
                  <Palette size={20} className="text-purple-500" />
                  Style Options
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4 space-y-4">
                {/* Card Style */}
                <div>
                  <Label className="text-gray-300 text-sm mb-2 block">Card Style</Label>
                  <div className="flex gap-2">
                    {['rounded', 'sharp', 'glass'].map((option) => (
                      <button
                        key={option}
                        onClick={() => handleStyleChange('cardStyle', option)}
                        className={`px-4 py-2 rounded-lg border-2 capitalize transition-all ${
                          style.cardStyle === option
                            ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                            : 'border-gray-700 text-gray-400 hover:border-gray-600'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Spacing */}
                <div>
                  <Label className="text-gray-300 text-sm mb-2 block">Spacing</Label>
                  <div className="flex gap-2">
                    {['compact', 'comfortable', 'spacious'].map((option) => (
                      <button
                        key={option}
                        onClick={() => handleStyleChange('spacing', option)}
                        className={`px-4 py-2 rounded-lg border-2 capitalize transition-all ${
                          style.spacing === option
                            ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                            : 'border-gray-700 text-gray-400 hover:border-gray-600'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Section Background */}
                <div>
                  <Label className="text-gray-300 text-sm mb-2 block">Section Background</Label>
                  <div className="flex gap-2">
                    {['solid', 'gradient'].map((option) => (
                      <button
                        key={option}
                        onClick={() => handleStyleChange('sectionBackground', option)}
                        className={`px-4 py-2 rounded-lg border-2 capitalize transition-all ${
                          style.sectionBackground === option
                            ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                            : 'border-gray-700 text-gray-400 hover:border-gray-600'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Animation Style */}
                <div>
                  <Label className="text-gray-300 text-sm mb-2 block">Animation Style</Label>
                  <div className="flex gap-2">
                    {['none', 'subtle', 'dynamic'].map((option) => (
                      <button
                        key={option}
                        onClick={() => handleStyleChange('animationStyle', option)}
                        className={`px-4 py-2 rounded-lg border-2 capitalize transition-all ${
                          style.animationStyle === option
                            ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                            : 'border-gray-700 text-gray-400 hover:border-gray-600'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Right Column: Preview */}
        <div className="glass p-6 rounded-3xl border border-orange-500/20 sticky top-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Grid3X3 size={24} className="text-orange-500" />
            Layout Preview
          </h3>
          <div className="space-y-2">
            <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg text-center">
              <span className="text-orange-400 text-sm font-medium">Hero (Always First)</span>
            </div>
            {visibleSections.filter(s => s.id !== 'hero').map((section, index) => (
              <div 
                key={section.id}
                className="p-3 bg-zinc-900 border border-gray-700 rounded-lg flex items-center gap-3"
              >
                <span className="text-gray-500 text-sm w-6">{index + 2}.</span>
                <span className="text-white">{section.name}</span>
              </div>
            ))}
            {visibleSections.length <= 1 && (
              <p className="text-gray-500 text-center py-4">No other sections visible</p>
            )}
          </div>
          
          {/* Style Preview */}
          <div className="mt-6 pt-6 border-t border-gray-700">
            <h4 className="text-gray-400 text-sm mb-3">Active Styles</h4>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded capitalize">
                {style.cardStyle} cards
              </span>
              <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded capitalize">
                {style.spacing} spacing
              </span>
              <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded capitalize">
                {style.sectionBackground} bg
              </span>
              <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded capitalize">
                {style.animationStyle} animation
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="glass p-4 rounded-xl border border-blue-500/20 bg-blue-500/5">
        <p className="text-blue-400 text-sm">
          💡 <strong>Tip:</strong> Drag sections to reorder them. The Hero section always stays at the top. Use the eye icon to show/hide sections.
        </p>
      </div>
    </div>
  );
};

export default LayoutEditor;
